import React, { useCallback, useMemo } from 'react'
import {
  H3,
  Subheading,
  Separator,
  Button,
  InfoSheetPanel,
  Row,
  Overline,
  InlineButton,
  ListItem,
  Card,
  List,
  useOverlay,
  BillboardButton,
  usePageWidth,
} from '@fs/zion-ui'
import { DocumentRecordPerson } from '@fs/zion-icon'
import { Trans, useTranslation } from 'react-i18next'
import { useFeatureFlag } from '@fs/zion-flags'
import {
  getNestedElementById,
  getPersonName,
  useSlsDispatchContext,
  useSlsHighlightsContext,
  useSlsStateContext,
} from '@fs/zion-sls'
import { useFamilyReviewStateContext, STEPS, useFamilyReviewDispatchContext } from '../../providers/FamilyReviewContext'
import { stepsMap } from '../../providers/FamilyReviewReducer'
import EditEventForm from './EditEventForm'
import { getAllEvents } from '../../utils/utils'
import ConfirmRemoveEventOverlay from '../confirmRemoveEventOverlay/ConfirmRemoveEventOverlay'
import { removeEventFromPerson } from '../../utils/saveFunctions'
import { showEventsHighlights } from '../../utils/highlightUtils'

export default function EditEvent({
  editEventOverlay,
  stepOnePartTwoOverlay,
  reviewPersonEventsOverlay,
  currentTask,
  editHighlightsOverlay,
  setSaveUpdateCalled,
  setCardState,
  updateStepThreeData,
  navigateToStepOne,
  handleEventTypeChange,
}) {
  const h = useSlsHighlightsContext()
  const [t] = useTranslation()
  const saveChangesFlag = useFeatureFlag('rip_sharedImageViewer_saveChanges')
  const state = useFamilyReviewStateContext()
  const dispatch = useFamilyReviewDispatchContext()
  const zionSLSState = useSlsStateContext()
  const zionSlsDispatch = useSlsDispatchContext()
  const atPageWidth = usePageWidth()
  const isSmallScreen = atPageWidth({ default: true, md: false })
  const confirmRemoveEventOverlay = useOverlay()
  const currentEvent = useMemo(
    () => getNestedElementById(currentTask?.subElements, state?.selectedEventId),
    [currentTask?.subElements, state?.selectedEventId]
  )
  const currentEventId = currentEvent?.id
  const isPrimaryEvent = currentEvent?.primary || false
  const currentEventPrimaryPerson = currentTask.subElements.find((person) => person?.id === currentEvent?.personId)
  const currentEventPersonName = currentEventPrimaryPerson
    ? getPersonName({ person: currentEventPrimaryPerson, justOneName: true })
    : ''

  const getTitle = () => {
    if (state.currentStep === STEPS.STEP_THREE) return t('family-review.step4.heading', 'Life Events')
    return t('family-review.step5.heading', 'Final Review')
  }

  const resetStepThree = useCallback(
    (markThisCardCorrect, highlights) => {
      const currentEvents = getAllEvents(currentTask)
      setCardState({
        elements: currentEvents,
        stepStateCards: state.stepThree.eventCards,
        key: 'eventId',
        updateStepData: updateStepThreeData,
        markThisCardCorrect,
        highlights,
      })
    },
    [currentTask, setCardState, state.stepThree.eventCards, updateStepThreeData]
  )

  const handleEditEventClose = useCallback(
    (markThisCardCorrect, highlights) => {
      editEventOverlay.close()
      if (state.currentStep === STEPS.STEP_THREE) {
        resetStepThree(markThisCardCorrect, highlights)
      } else {
        showEventsHighlights({
          h,
          currentTask,
          highlights: highlights || [
            ...h.getHighlights().mainEditHighlights,
            ...h.getHighlights().splitScreenEditHighlights,
          ],
          eventIds: [currentEventId],
          isSmallScreen,
          splitScreenPartitionKey: state.splitScreenPartitionKey,
        })
      }
    },
    [h, editEventOverlay, resetStepThree, currentEventId, state, currentTask, isSmallScreen]
  )

  // The back arrow on the panel was clicked
  const userClosedPanel = () => {
    const recentlyClearedHighlights = h.clearEdits().mainHighlights
    handleEditEventClose(null, recentlyClearedHighlights)
  }

  const cancelButtonButtonClicked = () => {
    const recentlyClearedMainHighlights = h.clearEdits().mainHighlights
    handleEditEventClose(null, recentlyClearedMainHighlights)
    if (state.currentStep === STEPS.STEP_FOUR) reviewPersonEventsOverlay.open()
  }
  const removeEvent = async () => {
    try {
      await removeEventFromPerson({
        partitionKey: zionSLSState.currentImageArk,
        personIds: currentEvent.superElements,
        eventId: currentEvent?.id,
        zionSLSState,
        zionSlsDispatch,
        userId: state.userId,
        saveChangesFlag,
      })
      setSaveUpdateCalled(true)
      handleEditEventClose()
      if (state.currentStep === STEPS.STEP_FOUR) reviewPersonEventsOverlay.open()
      confirmRemoveEventOverlay.close()
    } catch (err) {
      console.error(err)
    }
  }

  return (
    <>
      <InfoSheetPanel
        {...{ ...editEventOverlay, close: userClosedPanel }}
        title={getTitle()}
        headingColor="green"
        footer={<EditEventFooter {...{ cancelButtonButtonClicked }} />}
      >
        {editEventOverlay.isOpen && (
          <>
            <H3 size="H5">
              <Overline>
                {t('family-review.stepXof4', 'Step {step} of 4', {
                  step: state.currentStep === STEPS.STEP_THREE ? '3' : '4',
                })}
              </Overline>
              {isPrimaryEvent
                ? t('family-review.editPrimaryEvent.heading', 'Edit Primary Event')
                : t('family-review.editEvent.heading', 'Edit Event')}
              <Subheading>
                {t(
                  'family-review.editEvent.content',
                  'Fix any incorrect fields or highlights. Add or remove fields as necessary.'
                )}
              </Subheading>
            </H3>
            <Separator />
            {isPrimaryEvent && (
              <>
                <Card>
                  <List>
                    <ListItem
                      startElement={<ListItem.Icon Icon={DocumentRecordPerson} />}
                      primaryText={
                        <span>
                          <Trans i18nKey="family-review.editEvent.change-primary">
                            <span>This is the</span>
                            <strong>Primary Event</strong>
                            <span>for the</span>
                            <strong>Primary Person.</strong>
                            <span> To change </span>
                            <InlineButton
                              linkName="Edit Event Back To Step 1"
                              onClick={() => {
                                editEventOverlay.close()
                                dispatch({
                                  type: 'SET_CURRENT_STEP',
                                  value: stepsMap.STEP_ONE,
                                })
                                navigateToStepOne()
                                stepOnePartTwoOverlay.handleClick()
                              }}
                            >
                              Go back to step 1
                            </InlineButton>
                          </Trans>
                        </span>
                      }
                    />
                  </List>
                </Card>
                <List>
                  <ListItem
                    overline={t('family-review.addEvent.formLabel.person', 'Event Person')}
                    primaryText={currentEventPersonName}
                  />
                </List>
              </>
            )}
            <Separator size="xxs" />
            <EditEventForm
              key={`${state.selectedEventId}-panel`}
              reviewPersonEventsOverlay={reviewPersonEventsOverlay}
              currentTask={currentTask}
              editHighlightsOverlay={editHighlightsOverlay}
              setSaveUpdateCalled={setSaveUpdateCalled}
              handleEditEventClose={handleEditEventClose}
              handleEventTypeChange={handleEventTypeChange}
              isPrimaryEvent={isPrimaryEvent}
            />
            <Separator />
            <BillboardButton
              data-testid="remove-event-button"
              color="red"
              emphasis="medium"
              onClick={confirmRemoveEventOverlay.handleClick}
              disabled={isPrimaryEvent}
              linkName="Edit Event Remove"
            >
              {t('edit-index.remove-event.button', 'Remove Event')}
            </BillboardButton>
          </>
        )}
      </InfoSheetPanel>
      <ConfirmRemoveEventOverlay confirmRemoveEventOverlay={confirmRemoveEventOverlay} removeEvent={removeEvent} />
    </>
  )
}

function EditEventFooter({ cancelButtonButtonClicked }) {
  const [t] = useTranslation()
  return (
    <Row alignX="end">
      <Button onClick={cancelButtonButtonClicked} linkName="Edit Event Cancel" data-testid="edit-event-cancel-button">
        {t('edit-index.cancel.button', 'Cancel')}
      </Button>
      <Button
        emphasis="high"
        linkName="Edit Event Save"
        data-testid="edit-event-save-button"
        form="edit-event"
        type="submit"
      >
        {t('edit-index.save.button', 'Save')}
      </Button>
    </Row>
  )
}
